import L from 'leaflet';

import { fetchGeoJson } from './geoJsonApi';
import { handleApiError } from './apiErrorHandler';
import { createLayer } from './createLayer';
import { drawLoteOutline } from './drawLoteOutline';
import { buildGetFeatureInfoUrl, getProperWmsUrl } from './mapserverPopup';

import { wmsLayers } from '../config/wmsLayers';
import { layerNameToApiTable } from '../config/layerNameToApiTable';
import { baseLayers } from '../config/panelLayers';
import {
	popupMapserverFormatters,
	layersMapserverComPopup,
} from '../config/dictionaryPopup';
import { zIndexLayer } from './zIndexLayerDefine';
import { applyReviewStatusesToFeatures } from './pendingReviewApi';

const layerBlocoEntrega = 'Blocos de Entrega';
const BE_FILTER_SUFFIX = '__BE_FILTER__';
const PROJECT_BOUNDS = L.latLngBounds(
	L.latLng(-21.841104749065032, -44.54956054687501),
	L.latLng(-27.848790459862073, -60.36987304687501)
);
const FAIXAS_VOO_FOTO = 'Faixas de Voo Foto';

export const getBeFilterLayerKey = layerName =>
	`${layerName}::${BE_FILTER_SUFFIX}`;
const FAIXAS_VOO_LOTES = Array.from({ length: 12 }, (_, i) => `${FAIXAS_VOO_FOTO}::Lote ${i + 1}`);

// Adiciona/remove listeners de cancelamento de tiles para camadas de faixas de voo
const attachCancelPendingTiles = (wmsLayer, map) => {
	wmsLayer._cancelPendingTiles = () => {
		const tiles = wmsLayer._tiles || {};
		Object.keys(tiles).forEach(key => {
			const tile = tiles[key];
			if (tile && tile.el && !tile.loaded) {
				tile.el.src = L.Util.emptyImageUrl;
				delete tiles[key];
			}
		});
	};
	map.on('movestart zoomstart', wmsLayer._cancelPendingTiles);
};

// Carrega um único layer WMS de faixa de voo no mapa
const addFaixaVooLayer = (key, map, loadedLayers) => {
	const wmsLayer = wmsLayers[key];
	if (!wmsLayer || loadedLayers.current[key]) return;

	const paneName = `pane-${FAIXAS_VOO_FOTO}`;
	if (!map.getPane(paneName)) {
		const pane = map.createPane(paneName);
		// zIndex 250: acima do OSM (200) mas abaixo de todos os overlays (400+)
		pane.style.zIndex = 100;
	}

	wmsLayer.addTo(map);
	loadedLayers.current[key] = wmsLayer;
	attachCancelPendingTiles(wmsLayer, map);
};

// Remove um único layer WMS de faixa de voo do mapa
const removeFaixaVooLayer = (key, map, loadedLayers) => {
	const layer = loadedLayers.current[key];
	if (!layer) return;

	if (layer._cancelPendingTiles) {
		map.off('movestart zoomstart', layer._cancelPendingTiles);
		delete layer._cancelPendingTiles;
	}
	map.removeLayer(layer);
	delete loadedLayers.current[key];
};

export const loadGeoJson = async (
	layerName,
	map,
	loadedLayersRef,
	navigate,
	showToast,
	statusFilter = null,
	allStatus = false,
	onPopupOpen,
	beFilter = [],
	onPendingReviewOpen
) => {
	let offset = 0;
	let tooltips = [];
	let errorShownRef = { current: false };
	let loadingToast;
	const limit = 2700;
	const tableName = layerNameToApiTable[layerName];
	const hasBeFilter = Array.isArray(beFilter) && beFilter.length > 0;
	const key = hasBeFilter
		? getBeFilterLayerKey(layerName)
		: statusFilter
			? `${layerName}::${statusFilter}`
			: layerName;
	const paneId = `pane-${key.replace(/\s+/g, '-').toLowerCase()}`;
	const bounds =
		layerName === layerBlocoEntrega ? PROJECT_BOUNDS : map.getBounds();

	if (!tableName) return;

	try {
		while (true) {
			const token = JSON.parse(
				atob(localStorage.getItem('token') || '""')
			);
			const response = await fetchGeoJson({
				tableName,
				bounds,
				offset,
				limit,
				token,
			});

			if (response.status === 200) {
				const geojson = await response.json();
				if (!geojson.features || geojson.features.length === 0) break;

				// Garante que o estado persistido da revisão seja aplicado antes
				// de o Leaflet recriar os PINs em zoom/pan/reload do layer.
				await applyReviewStatusesToFeatures(
					geojson.features,
					tableName
				);

				const isLineGeometry =
					geojson.features[0]?.geometry?.type === 'MultiLineString';
				let layer = loadedLayersRef.current[key];
				if (!layer) {
					if (loadedLayersRef.current[key]) {
						map.removeLayer(loadedLayersRef.current[key]);
						delete loadedLayersRef.current[key];
					}

					let zIndex = zIndexLayer(layerName);
					let pane = map.getPane(paneId);

					if (!pane) {
						pane = map.createPane(paneId);
					}
					if (Object.keys(layerNameToApiTable).includes(layerName)) {
						pane.style.zIndex = zIndex;
					}

					tooltips = [];
					layer = createLayer(
						map,
						paneId,
						key,
						layerName,
						statusFilter,
						tooltips,
						isLineGeometry,
						onPopupOpen,
						beFilter,
						onPendingReviewOpen
					);
					layer._tooltips = tooltips;
					loadedLayersRef.current[key] = layer;

					if (layerName === layerBlocoEntrega && statusFilter) {
						if (!loadedLayersRef.current._blocosEntregaCarregados) {
							loadedLayersRef.current._blocosEntregaCarregados = {};
						}
						loadedLayersRef.current._blocosEntregaCarregados[statusFilter] = true;
					}
				}

				if (isLineGeometry) {
					const existingFids = new Set();
					layer.eachLayer(l => {
						const fid = l.feature?.properties?.ogc_fid;
						if (fid != null) existingFids.add(fid);
					});

					const newFeatures = geojson.features.filter(f => {
						const fid = f.properties?.ogc_fid;
						return fid != null && !existingFids.has(fid);
					});

					layer.addData(newFeatures);
				} else {
					if (offset === 0) {
						layer.clearLayers();
					}
					layer.addData(geojson.features);

					if (layerName === layerBlocoEntrega && statusFilter) {
						drawLoteOutline(layer, map, statusFilter);
					}
				}
				offset += limit;
			} else {
				handleApiError(
					response.status,
					loadingToast,
					showToast,
					navigate,
					errorShownRef
				);
				break;
			}
		}
	} catch (error) {
		handleApiError(0, loadingToast, showToast, navigate, errorShownRef);
	}
};


const normalizeBe = value => String(value ?? '').trim().toUpperCase();

export const loadAvailableBes = async (
	layerName,
	navigate,
	showToast = true
) => {
	const tableName = layerNameToApiTable[layerName];
	if (!tableName) return [];

	const limit = 2700;
	let offset = 0;
	const bes = new Map();
	const errorShownRef = { current: false };

	while (true) {
		const token = JSON.parse(atob(localStorage.getItem('token') || '""'));
		const response = await fetchGeoJson({
			tableName,
			bounds: PROJECT_BOUNDS,
			offset,
			limit,
			token,
		});

		if (response.status !== 200) {
			handleApiError(
				response.status,
				undefined,
				showToast,
				navigate,
				errorShownRef
			);
			const error = new Error(`Erro ao buscar BEs de ${layerName}`);
			error.apiHandled = true;
			throw error;
		}

		const geojson = await response.json();
		const features = geojson.features || [];

		if (features.length === 0) break;

		features.forEach(feature => {
			const rawBe = feature.properties?.be;
			if (rawBe === null || rawBe === undefined) return;

			const displayValue = String(rawBe).trim();
			const normalized = normalizeBe(displayValue);
			if (!normalized || bes.has(normalized)) return;

			bes.set(normalized, displayValue);
		});

		if (features.length < limit) break;
		offset += limit;
	}

	return Array.from(bes.values()).sort((a, b) =>
		a.localeCompare(b, 'pt-BR', {
			numeric: true,
			sensitivity: 'base',
		})
	);
};

export const removeBeFilterLayer = (layerName, map, loadedLayers) => {
	const key = getBeFilterLayerKey(layerName);
	const layer = loadedLayers.current[key];
	if (!layer) return;

	if (layer._tooltips) {
		layer._tooltips.forEach(tooltip => {
			if (map.hasLayer(tooltip)) map.removeLayer(tooltip);
		});
	}

	if (layer._toggleLabelHandler) {
		map.off('zoomend', layer._toggleLabelHandler);
	}

	if (layer._clickableLayers) {
		layer._clickableLayers.forEach(clickableLayer => {
			if (map.hasLayer(clickableLayer)) {
				map.removeLayer(clickableLayer);
			}
		});
		layer._clickableLayers = [];
	}

	if (map.hasLayer(layer)) {
		map.removeLayer(layer);
	}

	delete loadedLayers.current[key];
};

export const loadGeoJsonByBe = async (
	layerName,
	bes,
	map,
	loadedLayers,
	navigate,
	showToast = true,
	onPopupOpen,
	onPendingReviewOpen
) => {
	removeBeFilterLayer(layerName, map, loadedLayers);

	return loadGeoJson(
		layerName,
		map,
		loadedLayers,
		navigate,
		showToast,
		null,
		false,
		onPopupOpen,
		bes,
		onPendingReviewOpen
	);
};

export const handleCheckboxChange = async (
	e,
	map,
	loadedLayers,
	navigate,
	fullLayerName,
	options = {},
	allStatus = false,
	onLayerLoadSuccess
) => {
	e.stopPropagation();

	if (!fullLayerName) return;
	const [layerName, status] = fullLayerName.split('::');
	const isSubStatus = !!status;

	if (layerName === layerBlocoEntrega && !isSubStatus) {
		return; // Ignora clique no pai de Blocos de Entrega
	}

	// ---------------------------------------------------------------
	// FAIXAS DE VOO FOTO — lógica pai/filho
	// ---------------------------------------------------------------
	if (layerName === FAIXAS_VOO_FOTO) {
		if (e.target.checked) {
			if (!isSubStatus) {
				// Pai marcado → carrega todos os lotes
				FAIXAS_VOO_LOTES.forEach(key => addFaixaVooLayer(key, map, loadedLayers));
			} else {
				// Filho marcado → carrega apenas este lote
				addFaixaVooLayer(fullLayerName, map, loadedLayers);
			}
		} else {
			if (!isSubStatus) {
				// Pai desmarcado → remove todos os lotes
				FAIXAS_VOO_LOTES.forEach(key => removeFaixaVooLayer(key, map, loadedLayers));
			} else {
				// Filho desmarcado → remove apenas este lote
				removeFaixaVooLayer(fullLayerName, map, loadedLayers);
			}
		}
		return;
	}
	// ---------------------------------------------------------------

	if (e.target.checked) {
		if (!isSubStatus || layerName === 'Lotes do Projeto') {
			const zIndex = zIndexLayer(layerName);
			const paneName = `pane-${layerName}`;
			let pane = map.getPane(paneName);

			if (!pane) {
				pane = map.createPane(paneName);
			}
			pane.style.zIndex = zIndex;

			if (layerName === 'Lotes do Projeto') {
				if (!wmsLayers[fullLayerName]) {
					return;
				}
				loadedLayers.current[fullLayerName] = wmsLayers[fullLayerName];
				wmsLayers[fullLayerName].addTo(map);
			} else if (wmsLayers[layerName]) {
				const wmsLayer = wmsLayers[layerName];
				wmsLayer.addTo(map);
				loadedLayers.current[layerName] = wmsLayer;

				const layersComCancelamento = [
					'Perfilamento a LASER Aerotransportado',
				];
				if (layersComCancelamento.includes(layerName)) {
					attachCancelPendingTiles(wmsLayer, map);
				}

				// POPUP MAPSERVER
				if (layersMapserverComPopup.includes(layerName)) {
					const tileContainer = wmsLayer.getContainer?.();
					if (tileContainer) {
						tileContainer.style.cursor = 'pointer';
					}

					wmsLayer._onMapClick = e => {
						const url = buildGetFeatureInfoUrl(
							map,
							{
								...wmsLayer,
								_url: getProperWmsUrl(layerName),
							},
							e.latlng,
							'text/plain'
						);

						fetch(url)
							.then(res => res.text())
							.then(text => {
								if (!text.includes('no results')) {
									const formatter =
										popupMapserverFormatters[layerName] ||
										popupMapserverFormatters['default'];
									const html = formatter(text);

									L.popup()
										.setLatLng(e.latlng)
										.setContent(html)
										.openOn(map);
								}
							})
							.catch(console.error);
					};
					if (!wmsLayer._popupAttached) {
						map.on('click', wmsLayer._onMapClick);
						wmsLayer._popupAttached = true;
					}
				}
				return;
			} else {
				return loadGeoJson(
					layerName,
					map,
					loadedLayers,
					navigate,
					!options.silent,
					null,
					false,
					undefined,
					[],
					options.onPendingReviewOpen
				);
			}
		} else {
			return loadGeoJson(
				layerName,
				map,
				loadedLayers,
				navigate,
				!options.silent,
				status,
				allStatus,
				onLayerLoadSuccess,
				[],
				options.onPendingReviewOpen
			);
		}
	} else {
		const key = isSubStatus ? fullLayerName : layerName;
		const layer = loadedLayers.current[key];

		if (layer) {
			if (layer._popupAttached && layer._onMapClick) {
				map.off('click', layer._onMapClick);
				layer._popupAttached = false;
			}
			if (layer._cancelPendingTiles) {
				map.off('movestart zoomstart', layer._cancelPendingTiles);
				delete layer._cancelPendingTiles;
			}
			if (layer._outline && map.hasLayer(layer._outline)) {
				map.removeLayer(layer._outline);
			}

			if (layer._tooltips) {
				layer._tooltips.forEach(t => {
					if (map.hasLayer(t)) map.removeLayer(t);
				});
			}
			if (layer._toggleLabelHandler) {
				map.off('zoomend', layer._toggleLabelHandler);
			}

			delete loadedLayers.current[key];
			map.removeLayer(layer);

			if (layer._clickableLayers) {
				layer._clickableLayers.forEach(cl => {
					if (map.hasLayer(cl)) {
						map.removeLayer(cl);
					}
				});
				layer._clickableLayers = [];
			}

			if (layerName === layerBlocoEntrega) {
				map.eachLayer(l => {
					let lote;
					if (status?.includes('::')) {
						lote = status.split('::')[1];
					} else {
						lote = status.split(' ')[1];
					}
					if (
						l.options?.pane === 'outline-pane' &&
						l._lote === parseInt(lote)
					) {
						map.removeLayer(l);
					}
				});
			}

			if (layerName === layerBlocoEntrega) {
				const stillActive = Object.keys(loadedLayers.current).some(k =>
					k.startsWith(layerBlocoEntrega)
				);
				if (!stillActive) {
					const tooltipPane = document.querySelector(
						'.leaflet-tooltip-pane'
					);
					if (tooltipPane) tooltipPane.classList.add('hidden-labels');
				}
			}
		}
	}
};

export const handleBaseLayerChange = (
	layerName,
	map,
	loadedLayersRef,
	selectedBaseLayer,
	setSelectedBaseLayer
) => {
	if (selectedBaseLayer) {
		if (selectedBaseLayer === FAIXAS_VOO_FOTO) {
			FAIXAS_VOO_LOTES.forEach(key => removeFaixaVooLayer(key, map, loadedLayersRef));
		} else if (loadedLayersRef.current[selectedBaseLayer]) {
			map.removeLayer(loadedLayersRef.current[selectedBaseLayer]);
			delete loadedLayersRef.current[selectedBaseLayer];
		}
	}

	if (!layerName) {
		const whiteLayerConfig = baseLayers.find(l => l.name === 'Vazio');
		if (whiteLayerConfig) {
			whiteLayerConfig.layer.addTo(map);
			loadedLayersRef.current['Vazio'] = whiteLayerConfig.layer;
			setSelectedBaseLayer('Vazio');
		}
		return;
	}

	if (layerName === FAIXAS_VOO_FOTO) {
		FAIXAS_VOO_LOTES.forEach(key => addFaixaVooLayer(key, map, loadedLayersRef));
		setSelectedBaseLayer(FAIXAS_VOO_FOTO);
		return;
	}

	const baseLayerConfig = baseLayers.find(l => l.name === layerName);
	if (baseLayerConfig) {
		const newLayer = baseLayerConfig.layer;
		newLayer.addTo(map);
		loadedLayersRef.current[layerName] = newLayer;
		setSelectedBaseLayer(layerName);
	}
};